import React, { useState } from 'react';
import { 
  BookOpen, 
  Monitor, 
  Cpu, 
  Settings, 
  ChevronRight, 
  Download, 
  Wifi, 
  HardDrive,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Users,
  Building,
  UserCheck,
  CreditCard,
  Clock,
  Fingerprint,
  Power,
  Bluetooth,
  Lightbulb,
  Calendar

} from 'lucide-react';
import { useTheme } from '../../Context/themeContext';

const HelpSectionPage = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('website');
  const [searchQuery, setSearchQuery] = useState('');

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark 
    ? 'bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900' 
    : 'bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50';
  
  const bgCard = isDark ? 'bg-slate-900/60' : 'bg-white/80';
  const bgCardHover = isDark ? 'hover:border-blue-500/40' : 'hover:border-blue-400/60';
  const bgSection = isDark ? 'bg-slate-800/40' : 'bg-gray-100/80';
  const bgInput = isDark ? 'bg-slate-800/80' : 'bg-white/90';
  const bgTabActive = isDark ? 'bg-blue-600' : 'bg-blue-500';
  const bgTabInactive = isDark ? 'bg-slate-800/60' : 'bg-gray-200/80';
  
  const borderColor = isDark ? 'border-blue-500/20' : 'border-blue-300/40';
  //const borderHover = isDark ? 'border-blue-500/40' : 'border-blue-400/60';
  const borderInput = isDark ? 'border-blue-500/30' : 'border-blue-300/50';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  //const textSecondary = isDark ? 'text-blue-200' : 'text-gray-700';
  //const textMuted = isDark ? 'text-blue-200/80' : 'text-gray-600';
  //const textLight = isDark ? 'text-blue-300' : 'text-gray-600';
  const textPlaceholder = isDark ? 'placeholder-blue-300/50' : 'placeholder-gray-400';
  
  const shadowColor = isDark ? 'shadow-blue-500/25' : 'shadow-blue-400/30';
  const gridBg = isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)';
  
  const bgGradient1 = isDark 
    ? 'bg-gradient-to-r from-blue-600/10 to-indigo-600/10' 
    : 'bg-gradient-to-r from-blue-100/50 to-indigo-100/50';
  const bgGradient2 = isDark 
    ? 'bg-gradient-to-r from-slate-800/50 to-blue-900/30' 
    : 'bg-gradient-to-r from-gray-100/50 to-blue-100/50';

  // ========================
  // WEBSITE MANUAL (Attendance Management System)
  // ========================
  const websiteManual = {
    setup: {
      title: "🏢 Organization Onboarding & Account Setup",
      steps: [
        { id: 1, title: "Purchase Hardware & Submit Details", description: "After purchasing the hardware device, send payment proof and organization information (name, address, contact email, phone) to the admin via email/WhatsApp. The admin will verify and create your account.", icon: "HardDrive" },
        { id: 2, title: "Receive Login Credentials", description: "Admin sends unique username & password to the registered organization email. Use these credentials to access the Attendance Management System dashboard.", icon: "Download" },
        { id: 3, title: "Organization First Login", description: "Log in as Organization. You'll land on the dashboard showing wallet balance, device list, and user stats. Initial wallet gets ₹365 bonus per new device (added later).", icon: "Monitor" },
        { id: 4, title: "Create Manager Account", description: "Go to 'Managers' → 'Add Manager'. Fill name, email, role. Manager receives login credentials. Managers handle daily device operations & user enrollment.", icon: "Users" },
        { id: 5, title: "Wallet & Subscription Model", description: "Each new device adds ₹365 to wallet. Each enrolled user consumes wallet balance (deduction per active user per month). When wallet reaches zero, attendance records become hidden until recharge.", icon: "CreditCard" }
      ]
    },
    operation: {
      title: "📋 Manager & User Panel Operations",
      sections: [
        { name: "Manager Login & Dashboard", content: "Manager logs in using credentials sent by organization admin. Dashboard shows: active devices, total enrolled users, wallet status, recent attendance logs, and alert if wallet is low/expired." },
        { name: "Add New Device (Device Code Pairing)", content: "Go to 'Devices' section → 'Add New Device'. Enter the unique Device Code printed on the hardware device. Upon successful pairing, device appears online and ₹365 is credited to organization wallet automatically." },
        { name: "User Enrollment (Fingerprint/ID)", content: "Navigate to 'Enrollment' → 'New User'. Fill user details (name, ID, department). Select the target device from dropdown. Enroll fingerprint via connected hardware or upload manually. Success adds user to device memory." },
        { name: "View Attendance & Reports", content: "Access 'Attendance' tab to see real-time logs (date, time, user name, device). Export CSV/PDF. Note: If wallet balance = ₹0, attendance records become inaccessible until wallet top-up." },
        { name: "Leave Management (User & Manager)", content: "Users apply leave from 'leave Manage' → 'New Request' (select dates, reason). Manager sees pending requests in 'Leave Approvals' → Approve/Reject. Approved leave deducts from user's leave balance (e.g., annual quota)." },
        { name: "User Panel Overview", content: "Each user can log in to view personal attendance history, leave balance, remaining leaves, and apply for new leaves. Also shows device enrollment status and last 30 days attendance summary." },
        { name: "Wallet & Subscription Alerts", content: "When wallet balance drops below ₹100, system sends email/SMS alert to organization admin & manager. If wallet reaches ₹0, attendance data is hidden (but preserved). Recharge via online payment to restore access." }
      ]
    }
  };

  // ========================
  // HARDWARE MANUAL (Fingerprint Attendance Device)
  // ========================
  const hardwareManual = {
    setup: {
      title: "🔌 Hardware Installation & First Time Setup",
      steps: [
        { id: 1, title: "Unboxing & Components", description: "Inside the box: Attendance terminal, power adapter (5V/2A), mounting bracket, screws, Quick Start card with Device Code and Bluetooth name.", icon: "HardDrive" },
        { id: 2, title: "Power Connection & Boot", description: "Connect power adapter to device and wall socket. Press and hold power button for 3 seconds. Device beeps once → Power LED turns solid blue.", icon: "Power" },
        { id: 3, title: "Bluetooth & Wi-Fi Pairing", description: "On your phone/computer, enable Bluetooth. Scan for device name printed on hardware label (e.g., 'ATTEND-XXXX'). Pair (no password). Open the web dashboard to complete Wi-Fi setup via Bluetooth.", icon: "Bluetooth" },
        { id: 4, title: "Wi-Fi Configuration & LED Status", description: "Once Bluetooth connected, the device broadcasts a setup portal. Select your Wi-Fi SSID and enter password. After successful connection, the Wi-Fi LED turns solid GREEN.", icon: "Wifi" },
        { id: 5, title: "Device Code & Online Sync", description: "After Wi-Fi setup, the device syncs with cloud. Manager must add the Device Code (printed on device) in the website 'Devices' section. Upon successful pairing, device is ready for enrollment and attendance.", icon: "Cpu" }
      ]
    },
    operation: {
      title: "🖐️ Daily Operation & Fingerprint Attendance",
      sections: [
        { name: "Power On/Off & Idle Mode", content: "Single press power button: device goes to sleep (LED blinks slowly). Long press 5 seconds: shutdown. Idle mode after 30s inactivity – touch sensor to wake." },
        { name: "LED & Status Indicators", content: "🔵 Power LED solid = on. 🟢 Wi-Fi LED solid = connected. 🟡 Fingerprint LED flashes yellow = scanning. ✅ Green LED flash = attendance success. ❌ Red LED flash = failure (fingerprint not recognized)." },
        { name: "Mark Attendance (Fingerprint)", content: "User places registered finger on the sensor. Device captures fingerprint, matches locally/cloud. Green LED + beep = success → attendance logged with timestamp. Red LED + two beeps = failure; retry." },
        { name: "Voice/Beep Feedback", content: "Single beep: successful operation. Double beep: error. Three short beeps: low wallet warning (syncs with cloud). Device also displays user name on small screen if configured." },
        { name: "Enrollment via Device (Alternative)", content: "Manager can also enroll users directly on device: Enter admin mode (press * + #), select 'Enroll', scan fingerprint twice, enter user ID (mapped to cloud). Enrollment syncs to website automatically." },
        { name: "Troubleshooting Common Hardware Issues", content: "❌ No power: check adapter/cable. ❌ Wi-Fi LED off: network down or wrong password; reconfigure via Bluetooth. ❌ Red LED always: device memory full or not paired. ❌ Fingerprint not recognized: clean sensor with dry cloth, re-enroll user." },
        { name: "Firmware Updates & Maintenance", content: "Monthly cleaning of fingerprint sensor. Firmware updates are pushed OTA via website; device updates automatically overnight. To force update: press and hold both volume buttons during power on." }
      ]
    }
  };

  const currentManual = activeTab === 'website' ? websiteManual : hardwareManual;

  // Filter operation sections based on search query
  const filteredSections = currentManual.operation.sections.filter(section =>
    section.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper: render icon by name (dynamic)
  const renderIcon = (iconName, className = "w-5 h-5") => {
    const iconProps = { className, key: iconName };
    switch(iconName) {
      case 'Monitor': return <Monitor {...iconProps} />;
      case 'Download': return <Download {...iconProps} />;
      case 'Settings': return <Settings {...iconProps} />;
      case 'Shield': return <Shield {...iconProps} />;
      case 'Zap': return <Zap {...iconProps} />;
      case 'HardDrive': return <HardDrive {...iconProps} />;
      case 'Wifi': return <Wifi {...iconProps} />;
      case 'Cpu': return <Cpu {...iconProps} />;
      case 'Users': return <Users {...iconProps} />;
      case 'CreditCard': return <CreditCard {...iconProps} />;
      case 'Power': return <Power {...iconProps} />;
      case 'Bluetooth': return <Bluetooth {...iconProps} />;
      default: return <Building {...iconProps} />;
    }
  };

  return (
    <div className={`min-h-screen ${bgPrimary} transition-colors duration-300`}>
      {/* Header with background pattern */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-grid-pattern"
            style={{ 
              backgroundImage: `radial-gradient(circle, ${gridBg} 1px, transparent 1px)`,
              backgroundSize: '32px 32px'
            }}
          ></div>
        </div>
        
        <div className="container relative z-10 px-4 py-24 mx-auto">
          <div className="mb-12 text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 mb-6 border rounded-full ${isDark ? 'bg-blue-500/10 backdrop-blur-sm border-blue-400/30' : 'bg-blue-100/50 backdrop-blur-sm border-blue-300'} transition-colors duration-300`}>
              <BookOpen className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Attendance System · Complete Manual</span>
            </div>
            <h1 className={`mb-4 text-5xl font-bold md:text-6xl bg-gradient-to-r from-blue-400 via-blue-700 to-blue-500 text-transparent bg-clip-text`}>
              Admin & Hardware Hub
            </h1>
            <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>
              Organization onboarding · Manager workflows · User enrollment · Fingerprint device setup & attendance
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search guides: wallet, enrollment, device pairing, leave approval, LED codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full px-5 py-3 pl-12 transition-all border rounded-xl ${bgInput} ${borderInput} ${textPrimary} ${textPlaceholder} focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20`}
              />
              <HelpCircle className={`absolute left-4 top-3.5 w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={() => { setActiveTab('website'); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'website'
                  ? `${bgTabActive} text-white shadow-lg ${shadowColor} border border-blue-400`
                  : `${bgTabInactive} ${isDark ? 'text-blue-300 hover:bg-slate-700/60' : 'text-gray-700 hover:bg-gray-300/80'} border ${borderColor}`
              }`}
            >
              <Monitor className="w-5 h-5" />
              Website Manual (Org/Manager/User)
            </button>
            <button
              onClick={() => { setActiveTab('hardware'); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'hardware'
                  ? `${bgTabActive} text-white shadow-lg ${shadowColor} border border-blue-400`
                  : `${bgTabInactive} ${isDark ? 'text-blue-300 hover:bg-slate-700/60' : 'text-gray-700 hover:bg-gray-300/80'} border ${borderColor}`
              }`}
            >
              <Fingerprint className="w-5 h-5" />
              Hardware Manual (Attendance Device)
            </button>
          </div>

          {/* Main Content Grid */}
          <div className="grid gap-8 lg:grid-cols-2">
            {/* Setup Column */}
            <div className={`p-6 transition-all border ${bgCard} backdrop-blur-sm rounded-2xl ${borderColor} ${bgCardHover}`}>
              <div className={`flex items-center gap-3 pb-3 mb-6 border-b ${isDark ? 'border-blue-500/20' : 'border-blue-300/30'}`}>
                <Settings className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h2 className={`text-2xl font-bold ${textPrimary}`}>{currentManual.setup.title}</h2>
              </div>
              <div className="space-y-4">
                {currentManual.setup.steps.map((step) => (
                  <div key={step.id} className={`flex gap-3 p-3 transition-all rounded-lg ${isDark ? 'hover:bg-blue-500/5' : 'hover:bg-blue-50/50'}`}>
                    <div className={`flex items-center justify-center flex-shrink-0 w-8 h-8 text-sm font-bold rounded-full ${isDark ? 'text-blue-400 bg-blue-500/20' : 'text-blue-600 bg-blue-100'}`}>
                      {step.id}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {renderIcon(step.icon, `w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`)}
                        <h3 className={`font-semibold ${textPrimary}`}>{step.title}</h3>
                      </div>
                      <p className={`text-sm leading-relaxed ${isDark ? 'text-blue-200/80' : 'text-gray-600'}`}>{step.description}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition-colors ${isDark ? 'text-blue-500/30 group-hover:text-blue-400' : 'text-blue-400/30 group-hover:text-blue-600'}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Operation Column with scroll */}
            <div className={`p-6 transition-all border ${bgCard} backdrop-blur-sm rounded-2xl ${borderColor} ${bgCardHover}`}>
              <div className={`flex items-center gap-3 pb-3 mb-6 border-b ${isDark ? 'border-blue-500/20' : 'border-blue-300/30'}`}>
                <Zap className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h2 className={`text-2xl font-bold ${textPrimary}`}>{currentManual.operation.title}</h2>
              </div>
              <div className="space-y-4 max-h-[630px] overflow-y-auto pr-2 custom-scrollbar">
                {filteredSections.length > 0 ? (
                  filteredSections.map((section, idx) => (
                    <div key={idx} className={`p-4 transition-all border rounded-lg ${bgSection} ${borderColor} ${bgCardHover}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                            <CheckCircle className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className={`flex items-center gap-2 mb-2 font-semibold ${textPrimary}`}>
                            {section.name}
                            <ArrowRight className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'} opacity-0 group-hover:opacity-100 transition-opacity`} />
                          </h3>
                          <p className={`text-sm leading-relaxed ${isDark ? 'text-blue-200/80' : 'text-gray-600'}`}>{section.content}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center">
                    <AlertCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-blue-400/50' : 'text-blue-500/50'}`} />
                    <p className={`${isDark ? 'text-blue-300' : 'text-gray-700'}`}>No matching results found</p>
                    <p className={`mt-1 text-sm ${isDark ? 'text-blue-400/60' : 'text-gray-500'}`}>Try 'wallet', 'enrollment', 'device code' or 'LED'</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Tips + Critical Wallet & LED Info */}
          <div className="grid gap-6 mt-12 md:grid-cols-2">
            <div className={`p-6 border ${bgGradient1} rounded-2xl ${borderColor}`}>
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                <h3 className={`text-lg font-semibold ${textPrimary}`}>💰 Wallet & Subscription Rules</h3>
              </div>
              <ul className={`space-y-2 text-sm ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>
                <li className="flex gap-2"><CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'} mt-0.5`} /> ✅ Each new hardware device adds ₹365 to organization wallet automatically after pairing.</li>
                <li className="flex gap-2"><CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'} mt-0.5`} /> ✅ Wallet deducts based on active enrolled users (monthly per user).</li>
                <li className="flex gap-2"><AlertCircle className={`w-4 h-4 ${isDark ? 'text-yellow-400' : 'text-yellow-600'} mt-0.5`} /> ⚠️ Zero balance → Attendance records become hidden (manager/org cannot view logs) until recharge.</li>
                <li className="flex gap-2"><Shield className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'} mt-0.5`} /> 🔁 Recharge via online payment gateway from organization dashboard.</li>
                <li className="flex gap-2"><Clock className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'} mt-0.5`} /> 📅 Leave deductions: Approved leaves subtract from user’s leave balance (based on policy set by org).</li>
              </ul>
            </div>

            <div className={`p-6 border ${bgGradient2} rounded-2xl ${borderColor}`}>
              <div className="flex items-center gap-3 mb-4">
                <Lightbulb className={`w-5 h-5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <h3 className={`text-lg font-semibold ${textPrimary}`}>💡 Hardware LED Quick Reference</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-500 rounded-full"></div><span className={isDark ? 'text-blue-200' : 'text-gray-700'}>Power solid blue: ON</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full"></div><span className={isDark ? 'text-blue-200' : 'text-gray-700'}>WiFi solid green: Connected</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-400 rounded-full"></div><span className={isDark ? 'text-blue-200' : 'text-gray-700'}>Yellow flash: Scanning FP</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-400 rounded-full"></div><span className={isDark ? 'text-blue-200' : 'text-gray-700'}>Green flash + beep: Success ✅</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full"></div><span className={isDark ? 'text-blue-200' : 'text-gray-700'}>Red flash + 2 beeps: Fail ❌</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-500"></div><span className={isDark ? 'text-blue-200' : 'text-gray-700'}>Amber blink: Low wallet warning</span></div>
              </div>
              <div className={`pt-3 mt-4 text-xs border-t ${isDark ? 'border-blue-500/20 text-blue-300/70' : 'border-gray-300/50 text-gray-500'}`}>
                📡 Bluetooth pairing: Device name printed on label | Pair to configure Wi-Fi.
              </div>
            </div>
          </div>

          {/* Enrollment & Leave Workflow Summary Card */}
          <div className={`p-6 mt-6 border ${bgCard} backdrop-blur-sm rounded-2xl ${borderColor}`}>
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="flex items-center gap-3">
                <UserCheck className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h4 className={`font-semibold ${textPrimary}`}>Manager Enrollment Process</h4>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>Device added → Enroll user → Select device → Capture fingerprint → Success → User can punch attendance.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h4 className={`font-semibold ${textPrimary}`}>Leave Workflow (User → Manager)</h4>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>User applies → Manager approves → Leaves deducted from user quota. Real-time sync.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h4 className={`font-semibold ${textPrimary}`}>Organization → Manager → User</h4>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>Hierarchy: Org creates manager, manager adds devices & enrolls users, each user has own panel.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)'};
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${isDark ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.3)'};
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.5)'};
        }
      `}</style>
    </div>
  );
};

export default HelpSectionPage;
